import { useMemo } from 'react';
import { formatCurrency } from '../components/helpers';

export const MONTHS = [
    { key: 'april', label: 'Apr' },
    { key: 'may', label: 'May' },
    { key: 'june', label: 'Jun' },
    { key: 'july', label: 'Jul' },
    { key: 'august', label: 'Aug' },
    { key: 'september', label: 'Sep' },
    { key: 'october', label: 'Oct' },
    { key: 'november', label: 'Nov' },
    { key: 'december', label: 'Dec' },
    { key: 'january', label: 'Jan' },
    { key: 'february', label: 'Feb' },
    { key: 'march', label: 'Mar' },
];

export const rowTotal = (row) => {
    if (typeof row.total === 'number') return row.total;
    return MONTHS.reduce((s, m) => s + (Number(row[m.key]) || 0), 0);
};

export const monthlyColumnTotals = (rows, totals) => {
    if (totals) return totals;
    const t = {};
    MONTHS.forEach((m) => {
        t[m.key] = rows.reduce((s, r) => s + (Number(r[m.key]) || 0), 0);
    });
    t.total = rows.reduce((s, r) => s + rowTotal(r), 0);
    return t;
};

const MonthlyIncomeTable = ({ rows = [], totals }) => {
    const columnTotals = useMemo(() => monthlyColumnTotals(rows, totals), [rows, totals]);

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-xs">
                <thead>
                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                        <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px] tw-sticky tw-left-0 tw-bg-gray-50 tw-z-10">
                            Account Name
                        </th>
                        {MONTHS.map((m) => (
                            <th key={m.key} className="tw-text-right tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px] tw-whitespace-nowrap">
                                {m.label}
                            </th>
                        ))}
                        <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-800 tw-text-[11px] tw-whitespace-nowrap tw-bg-gray-100">
                            Total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={MONTHS.length + 2} className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                No data available
                            </td>
                        </tr>
                    )}
                    {rows.map((row, idx) => {
                        const total = rowTotal(row);
                        const name = row.name || row.accountName || row.parentAccountName || '';
                        const code = row.ac_code || row.code || row.accountCode || '';
                        return (
                            <tr key={row.id ?? code ?? idx} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50 tw-transition-colors">
                                <td className="tw-px-4 tw-py-2 tw-text-gray-700 tw-sticky tw-left-0 tw-bg-white">
                                    <span className="tw-text-blue-700">
                                        {name}{code ? ` (${code})` : ''}
                                    </span>
                                </td>
                                {MONTHS.map((m) => (
                                    <td key={m.key} className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-700">
                                        {formatCurrency(row[m.key], { dashOnZero: true })}
                                    </td>
                                ))}
                                <td className="tw-px-4 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-semibold tw-text-gray-800 tw-bg-gray-50">
                                    {formatCurrency(total, { dashOnZero: true })}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                {rows.length > 0 && (
                    <tfoot>
                        <tr className="tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-font-bold">
                            <td className="tw-px-4 tw-py-3 tw-text-gray-800 tw-sticky tw-left-0 tw-bg-gray-100">Total</td>
                            {MONTHS.map((m) => (
                                <td key={m.key} className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-900 tw-whitespace-nowrap">
                                    {formatCurrency(columnTotals[m.key], { dashOnZero: true })}
                                </td>
                            ))}
                            <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-900 tw-whitespace-nowrap tw-bg-gray-200">
                                {formatCurrency(columnTotals.total, { dashOnZero: true })}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default MonthlyIncomeTable;
